import React from 'react';
import {isRouteResult, NavigationProvider, useRoute} from '@sail/navigation';
import type {FullNavigationConfig} from '@sail/navigation';
import {useIntl} from '@sail/intl';

import App from 'src/App';
import FundingSessionApp from 'src/fund/FundingSessionApp';
import {FundingSessionContextProvider} from 'src/fund/state/FundingSessionContext';
import ReturnApp from 'src/return/App';
import {ReturnDetailsCollectionContextProvider} from 'src/return/state/ReturnDetailsCollectionContext';
import {PayinSessionContextProvider} from 'src/state/PayinSessionContext';
import {UserFacingError} from 'src/utils/error';
import messages from 'src/utils/messages';
import routes from 'src/v2/state/routing/routes';

// TODO: `setHref` is a no-op — nothing calls `setRoute` today. Wire it to
// `history.pushState`/`replaceState` plus a href state updater once we need
// to navigate between routes programmatically from within the app.
const noopSetHref: FullNavigationConfig['setHref'] = () => undefined;

/**
 * Inner component that reads the current route and renders the
 * appropriate view. Must be rendered inside NavigationProvider.
 */
const Routes = (): React.ReactElement => {
  const [route] = useRoute();
  const intl = useIntl();

  if (isRouteResult(route, 'PAY') || isRouteResult(route, 'SETUP')) {
    return (
      <PayinSessionContextProvider>
        <App />
      </PayinSessionContextProvider>
    );
  }

  if (isRouteResult(route, 'RETURN')) {
    return (
      <ReturnDetailsCollectionContextProvider
        sessionHash={route.params.sessionHash}
      >
        <ReturnApp />
      </ReturnDetailsCollectionContextProvider>
    );
  }

  if (isRouteResult(route, 'FUND')) {
    return (
      <FundingSessionContextProvider sessionHash={route.params.sessionHash}>
        {/* The data-v2 query token carries the UAT returned by resume. */}
        {(token) => <FundingSessionApp token={token} />}
      </FundingSessionContextProvider>
    );
  }

  throw new UserFacingError(intl.formatMessage(messages.error404));
};

/**
 * Top-level router for the crypto payins app.
 */
export const Router = (): React.ReactElement => {
  const config = React.useMemo<FullNavigationConfig>(
    () => ({
      href: window.location.href,
      setHref: noopSetHref,
      routes,
    }),
    [],
  );

  return (
    <NavigationProvider config={config}>
      <Routes />
    </NavigationProvider>
  );
};
